/**
 * Backend auth API — sessions list + revoke hooks.
 *
 * Wraps GET /v1/auth/sessions + DELETE /v1/auth/sessions/{sessionId} on
 * the main-api-audit Function App. The backend currently returns a
 * single synthetic "current" session per request (Phase 11 dev shape)
 * with `noticeFlags.syntheticSessions` set — the frontend renders that
 * note inline so the user sees the same truth the server has.
 *
 * MFA factor management on /app/security goes via the Supabase JS SDK
 * directly (auth.mfa.listFactors / mfa.unenroll) — it doesn't need a
 * backend hop. Sessions do need a backend hop because they live in
 * Supabase's session store, which the client SDK only sees for the
 * current session.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';

export interface AuthSessionView {
  id: string;
  issuedAt: number;
  expiresAt: number;
  current: boolean;
}

export interface AuthSessionsListResponse {
  sessions: AuthSessionView[];
  correlationId: string;
  noticeFlags?: { syntheticSessions?: boolean };
}

export function useAuthSessions() {
  return useQuery<AuthSessionsListResponse>({
    queryKey: ['auth', 'sessions'],
    queryFn: ({ signal }) =>
      // S-1.1: sessions are the operator's own — never the act-as client's.
      api<AuthSessionsListResponse>('/api/v1/auth/sessions', {
        signal,
        useOperatorSession: true,
      }),
  });
}

export function useRevokeAuthSession() {
  const qc = useQueryClient();
  return useMutation<void, Error, { sessionId: string }>({
    mutationFn: ({ sessionId }) =>
      // S-1.1: revoke the OPERATOR's own session — never the act-as client's.
      api<void>(`/api/v1/auth/sessions/${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
        useOperatorSession: true,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
  });
}

// ── MFA backup codes ─────────────────────────────────────────────────────────
//
// FE-6 (Henry R2-004): the Security Centre previously claimed single-use
// recovery codes were "not yet issued by Elreign — coming in a later release".
// That was false — the backend GET /v1/me/backup-codes endpoint mints + returns
// 10 single-use codes (hashed at rest, shown ONCE), and `?regenerate=true`
// rotates them (step-up gated). These hooks surface that real capability.
//
// See backend/src/handlers/me-backup-codes-get.ts:
//   - first call (no codes yet): 200 with `codes: string[]`
//   - call without ?regenerate when codes exist: 409 ALREADY_ISSUED + remainingCount
//   - ?regenerate=true: 200 with a fresh set
//   - EVERY call (first issuance included, since QA P0-1 on 2026-10-04) needs a
//     fresh step-up; a stale session gets 403 STEP_UP_REQUIRED, which the
//     global StepUpInterceptor turns into the modal + replay.

export interface BackupCodesResponse {
  codes: string[];
  issuedAt: string;
  regenerated: boolean;
  warning: string;
  correlationId: string;
}

/** Backend 409 body when codes already exist and `regenerate` was not set. */
export interface BackupCodesAlreadyIssued {
  error: string;
  code: 'ALREADY_ISSUED';
  message?: string;
  remainingCount?: number;
  issuedAt?: string;
}

/**
 * Issue (first time) or regenerate the caller's MFA backup codes.
 *
 * Pass `{ regenerate: true }` to rotate an existing set. The backend requires a
 * fresh step-up for issuance AND regeneration (QA P0-1: a password-only
 * session used to be able to mint the first set) and answers 403
 * STEP_UP_REQUIRED otherwise (ApiError.isStepUpRequired). The global
 * StepUpInterceptor replays the mutation after the challenge, so a caller must
 * read the outcome from the mutation STATE (`data` / `error`), not only from
 * the promise of the first call.
 *
 * On a non-regenerate call when codes already exist the backend returns 409
 * ALREADY_ISSUED; callers read `ApiError.body` (BackupCodesAlreadyIssued) to
 * show "you still have N codes" rather than re-leaking plaintext.
 */
export function useIssueBackupCodes() {
  return useMutation<BackupCodesResponse, ApiError, { regenerate?: boolean } | void>({
    mutationFn: (vars) => {
      const regenerate = vars && vars.regenerate === true;
      const path = regenerate
        ? '/api/v1/me/backup-codes?regenerate=true'
        : '/api/v1/me/backup-codes';
      // S-1.1: backup codes are the operator's own MFA — never the act-as client's.
      return api<BackupCodesResponse>(path, { useOperatorSession: true });
    },
  });
}

// ── Backup-code recovery pair (lane C · C5 + C6, 2026-07-09) ─────────────────
//
// The pre-auth, ANONYMOUS self-serve recovery flow for "I have my email +
// password but lost my MFA device". Two-step pair, always used together:
//
//   1. POST /v1/auth/backup-codes/verify `{ email, code }` — consumes the
//      matching single-use backup code and mints a SIGNED, expiring,
//      single-use recovery token (auth-backup-codes-verify.ts).
//   2. POST /v1/auth/recovery/mfa-reset `{ email, recoveryToken }` — redeems
//      that token and DELETES every MFA factor on the account through the
//      Supabase admin API, then records the count in user_metadata; the user
//      enrols a new authenticator at their next sign-in. Sends a security
//      receipt email (auth-recovery-mfa-reset.ts).
//
//      Until 2026-09-21 this only wrote a `mfa_factors_reset_required` flag
//      that nothing in the app ever read (ELR-10), so the reset reset nothing.
//      A 200 from this endpoint now means the factors are actually gone.
//
// SECURITY CONTRACT the UI must mirror EXACTLY (existence-leak posture):
//   - verify failure is ALWAYS 400 INVALID_CODE with one generic message for
//     unknown email / malformed code / no match / already-consumed — callers
//     must never render distinct copy per cause;
//   - reset failure is ALWAYS 401 INVALID_RECOVERY_TOKEN, same rule;
//   - both endpoints run a per-IP damper (5/min) → 429 is the
//     attempts-exhausted state and must be rendered explicitly;
//   - reset answers 502 with TWO codes and they are not interchangeable:
//       · BAD_GATEWAY_SUPABASE: the code was CONSUMED before the Supabase hop
//         failed and NOTHING was destroyed. The honest recovery is "use a
//         different code".
//       · MFA_RESET_INCOMPLETE: factors WERE destroyed and the reset then
//         stopped. Rendering the different-code advice here is wrong twice: it
//         spends a second code on a reset that cannot finish, and it says
//         nothing about the state the account is actually in.
//
//         That state is one of TWO, and the body says which. Some factors gone
//         and one surviving: the reader must not lean on a factor we cannot
//         vouch for. EVERY factor gone with a later step failed: nothing
//         survives and the password is all that is left on the account until
//         they enrol a new authenticator. Do not paraphrase either of them
//         here. Render the body's own `message`, which is generated from the
//         listed-versus-deleted counts the handler actually achieved.
//         `/auth/recovery` branches on `err.code` and prints that sentence.
//
// No bearer is attached: callers have no session (that's the point), and
// api() degrades to an unauthenticated call when getSession() is empty.

export interface VerifyBackupCodeResponse {
  /** Signed single-use recovery token — feed straight into useMfaReset. */
  recoveryToken: string;
  expiresInSeconds: number;
  correlationId: string;
}

export function useVerifyBackupCode() {
  return useMutation<VerifyBackupCodeResponse, ApiError, { email: string; code: string }>({
    mutationFn: (body) =>
      api<VerifyBackupCodeResponse>('/api/v1/auth/backup-codes/verify', {
        method: 'POST',
        body,
      }),
  });
}

export interface MfaResetResponse {
  ok: true;
  correlationId: string;
}

export function useMfaReset() {
  return useMutation<MfaResetResponse, ApiError, { email: string; recoveryToken: string }>({
    mutationFn: (body) =>
      api<MfaResetResponse>('/api/v1/auth/recovery/mfa-reset', {
        method: 'POST',
        body,
      }),
  });
}
